import React, { useEffect, useState } from 'react';
import theme from './BuyerOptionCard.module.scss';
import { Button, Card, Badge, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import SmallAttachmentList from '@/components/SmallAttachmentList';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { TextView, classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { useIsMobile } from '@/utils/hooks';
import { BuyerOptionCardPrice } from './BuyerOptionCardPrice';
import { ConfiguratorOptionDto } from '@/services/configurator';
import { OptionMoreInfoModal } from '../OptionMoreInfoModal/OptionMoreInfoModal';
import { ProductThumbnails } from '@/seller/components/products/ProductThumbnails';
import { OptionSelectButton } from './OptionSelectButton';
import { useOptionSelection } from './useOptionSelection';
import { ProductType } from '@/types';
import { checkIfNCSCodeIsValid } from '@/utils/color';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { useImageUrls } from '../useImageUrls';
import { useConfiguratorMutations } from '../ConfiguratorMutationContext';

export interface BuyerOptionCardProps {
    option: ConfiguratorOptionDto;
    isSelected?: boolean;
    groupName?: string;
    is3d?: boolean;
    /// Hide the selection slot on this card entirely. Use when rendering option groups purely as
    /// previews (e.g. inside style details or the signing summary). For "whole configurator
    /// view-only", use the context's `viewOnly` flag instead.
    hideSelectionSlot?: boolean;
    renderSelectButton?: (option: ConfiguratorOptionDto) => React.ReactNode;
    /**
     * Renders the compact card as a *controlled* selector: it reflects `selected`/`disabled` from
     * these props and calls `onSelect` instead of performing a real configurator selection. Used by
     * the dependency wizard, where picks are pending until the user confirms. `invalid` marks a
     * selected-but-incompatible option (red indicator instead of the green checkmark).
     */
    compactSelection?: { selected: boolean; disabled: boolean; invalid?: boolean; onSelect: () => void };
}

export const BuyerOptionCard: React.FC<BuyerOptionCardProps> = ({
    option,
    groupName,
    is3d,
    renderSelectButton,
    hideSelectionSlot,
    compactSelection,
}) => {
    const { viewOnly, immersive } = useOptionConfigurator();
    const { getProjectCatalogProductFileUrl, getProjectCatalogProductFileThumbnailUrl } = useImageUrls();
    const { selectOption, isSelecting } = useConfiguratorMutations();
    const selection = useOptionSelection(option);
    const isMobile = useIsMobile();
    const product = option.product!;
    const [moreDetailsModalIsOpen, setMoreDetailsModalIsOpen] = useState(false);
    const [customFieldData, setCustomFieldData] = useState(option.customFieldData ?? '');
    const isCustomPaint = product?.type === ProductType.CustomPaint;
    const canEditCustomField = option.selected && !hideSelectionSlot && !viewOnly && isCustomPaint;
    const isCustomFieldValid = !option.selected || checkIfNCSCodeIsValid(customFieldData);

    const variant: 'threeD' | 'standard' = is3d ? 'threeD' : 'standard';

    const ui = {
        showHeaderPrice: !isMobile,
        showFooterPrice: isMobile,
        showDesktopThumbs: !isMobile && variant === 'standard',
        showMobileThumbs: variant === 'standard' && isMobile && !product?.isNone && (product?.images?.length ?? 0) > 0,
    };

    useEffect(() => {
        setCustomFieldData(option.customFieldData ?? '');
    }, [option.id, option.customFieldData]);

    // Per-card "selecting" flag for the compact card's image spinner. `isSelecting` is global to the
    // configurator (true for ANY in-flight selection), so we track locally that THIS card kicked off
    // the roundtrip and clear it once the mutation settles (or the option lands selected).
    const [pendingSelect, setPendingSelect] = useState(false);
    useEffect(() => {
        if (!isSelecting) setPendingSelect(false);
    }, [isSelecting, option.selected]);

    const handleCustomFieldBlur = () => {
        if (!canEditCustomField) {
            return;
        }

        if (!isCustomFieldValid) {
            return;
        }

        if (customFieldData === (option.customFieldData ?? '')) {
            return;
        }

        // Keep the latest custom input in sync with the selected option.
        selectOption({ option, customFieldData });
    };

    const handleCustomFieldKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.currentTarget.blur();
        }
    };

    // Compact immersive option card: image on the left (with a selection border + corner
    // checkmark), product name and price stacked on the right, "More details" inline with the
    // price. The whole card is the selection control — there's no separate select button.
    if ((immersive || !!compactSelection) && !hideSelectionSlot && !renderSelectButton) {
        // When `compactSelection` is supplied the card is a *controlled* selector (dependency
        // wizard): its selected/disabled state and click come from the caller, not the live
        // configurator selection. Otherwise it drives a real selection roundtrip.
        const isSelected = compactSelection ? compactSelection.selected : selection.selected;
        const isDisabled = compactSelection
            ? compactSelection.disabled && !compactSelection.selected
            : selection.disabled && !selection.selected;
        // A selected-but-incompatible pick (only reachable in the controlled/wizard mode) — show the
        // red warning indicator instead of the green checkmark.
        const isInvalid = !!compactSelection?.invalid && compactSelection.selected;
        const handleCompactSelect = () => {
            if (compactSelection) {
                if (!compactSelection.disabled) compactSelection.onSelect();
                return;
            }
            // A click while THIS card's own roundtrip is still in flight is an impatient re-click or
            // a double-click, not a deliberate toggle-off: the optimistic patch flips `selected` the
            // instant the first click lands, so the second one would otherwise fall into the deselect
            // branch below and undo the pick. Swallow it until the mutation settles.
            if (pendingSelect) return;
            if (selection.selected) {
                // Clicking the already-selected option clears it. Show the spinner for the clear
                // roundtrip unless the group is gated (view-only/expired/locked), where it's a no-op.
                if (!selection.disabled) setPendingSelect(true);
                selection.deselect();
                return;
            }
            // Show the spinner only for a real selection roundtrip — not when the click is a no-op
            // (disabled) or opens the dependency wizard (no immediate server call).
            if (!selection.disabled && !selection.triggersWizard) setPendingSelect(true);
            selection.select();
        };
        const card = (
            <Card
                className={classnames(theme.productCard, theme.productCardCompact, {
                    [theme.productCardBordered]: !!compactSelection,
                    [theme.selected]: isSelected,
                    [theme.disabled]: isDisabled,
                })}
                onClick={handleCompactSelect}>
                <div
                    className={classnames(theme.imageButton, {
                        [theme.imageSelected]: isSelected && !isInvalid,
                        [theme.imageInvalid]: isInvalid,
                    })}>
                    <ProductThumbnails
                        imageUrlGenerator={getProjectCatalogProductFileUrl}
                        thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                        images={product?.images || []}
                        disableOnClick
                        hideImageCount
                        size={56}
                        className={theme.compactThumbnail}
                    />
                    {!compactSelection && pendingSelect && (
                        <span className={theme.imageSpinner}>
                            <FontAwesomeIcon icon={faSpinner} spin />
                        </span>
                    )}
                    {isInvalid ? (
                        <span className={theme.checkmarkInvalid}>
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                        </span>
                    ) : (
                        isSelected && (
                            <span className={theme.checkmark}>
                                <FontAwesomeIcon icon={faCheck} />
                            </span>
                        )
                    )}
                </div>

                <div className={theme.compactInfo}>
                    <strong className={theme.compactName} title={product?.name}>
                        {product?.name}
                    </strong>
                    <div className={theme.compactBottom}>
                        {/* Keep the "More details" click from bubbling up to the card's select handler. */}
                        <span onClick={(e) => e.stopPropagation()}>
                            <Button
                                variant="link"
                                size="sm"
                                className={theme.moreDetailsLink}
                                onClick={() => setMoreDetailsModalIsOpen(true)}>
                                <Trans>More details</Trans>
                            </Button>
                        </span>
                        <BuyerOptionCardPrice option={option} />
                    </div>
                </div>

                {!compactSelection && selection.wizard}
                <OptionMoreInfoModal
                    option={option}
                    isOpen={moreDetailsModalIsOpen}
                    toggleModal={() => setMoreDetailsModalIsOpen(false)}
                />
            </Card>
        );

        return !compactSelection && selection.tooltip && !selection.selected ? (
            <SimpleTooltip content={selection.tooltip}>{card}</SimpleTooltip>
        ) : (
            card
        );
    }

    return (
        <Card className={theme.productCard}>
            {variant === 'standard' && ui.showDesktopThumbs && (
                <ProductThumbnails
                    imageUrlGenerator={getProjectCatalogProductFileUrl}
                    thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                    images={product?.images || []}
                    className={theme.thumbnail}
                />
            )}

            {variant === 'threeD' && (
                <ProductThumbnails
                    imageUrlGenerator={getProjectCatalogProductFileUrl}
                    thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                    disableOnClick
                    images={product?.images || []}
                    className={theme.thumbnail3d}
                />
            )}

            <div className={theme.mainSection}>
                <div className={theme.header}>
                    {variant === 'standard' && <div>{groupName}</div>}
                    <div className={theme.rightHeader}>
                        {option.isDefault && (
                            <Badge appearance="subtle" variant="info">
                                <Trans>Default</Trans>
                            </Badge>
                        )}
                        {(option.presets.some((p) => p.isSelected)
                            ? option.presets.filter((p) => p.isSelected)
                            : option.presets
                        ).map((preset) => (
                            <Badge key={preset.presetId}>{preset.presetName}</Badge>
                        ))}

                        {ui.showHeaderPrice && <BuyerOptionCardPrice option={option} />}
                    </div>
                </div>

                {ui.showMobileThumbs && (
                    <ProductThumbnails
                        imageUrlGenerator={getProjectCatalogProductFileUrl}
                        thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                        images={product!.images}
                        className={theme.thumbnail}
                    />
                )}

                <strong>{product?.name}</strong>

                {variant === 'standard' && (
                    <div className={theme.description}>
                        <TextView numberOfLines={2}>
                            <div>{product?.description}</div>
                        </TextView>
                    </div>
                )}

                {variant === 'standard' && isCustomPaint && (
                    <div className={theme.customPaintRow}>
                        <input
                            aria-label={t`NCS input`}
                            className={`form-control ${option.selected && !isCustomFieldValid ? 'is-invalid' : ''}`}
                            placeholder={t`Enter an NCS code`}
                            value={customFieldData}
                            disabled={!canEditCustomField || isSelecting}
                            onChange={(e) => setCustomFieldData(e.target.value)}
                            onKeyDown={handleCustomFieldKeyDown}
                            onBlur={handleCustomFieldBlur}
                        />
                        {option.selected && !isCustomFieldValid && (
                            <small className={theme.customPaintValidationMessage}>
                                <Trans>Not a valid NCS code</Trans>
                            </small>
                        )}
                    </div>
                )}

                <SmallAttachmentList
                    className={theme.attachments}
                    urlGenerator={(e) => getProjectCatalogProductFileUrl(e.id)}
                    attachments={product?.attachments || []}
                />

                <div className={theme.bottomRow}>
                    <div className={variant === 'threeD' ? theme.buttons3d : theme.buttons}>
                        <Button
                            size={variant === 'threeD' ? 'xs' : 'sm'}
                            variant="tertiary"
                            onClick={() => setMoreDetailsModalIsOpen(true)}>
                            <Trans>More details</Trans>
                        </Button>
                        {hideSelectionSlot ? null : renderSelectButton ? (
                            renderSelectButton(option)
                        ) : (
                            <OptionSelectButton option={option} size={variant === 'threeD' ? 'xs' : 'sm'} />
                        )}
                    </div>
                    {variant === 'standard' && ui.showFooterPrice && <BuyerOptionCardPrice option={option} />}
                </div>
            </div>

            <OptionMoreInfoModal
                option={option}
                isOpen={moreDetailsModalIsOpen}
                toggleModal={() => setMoreDetailsModalIsOpen(false)}
            />
        </Card>
    );
};
